import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Check, Users, Building2, Landmark } from "lucide-react";
import BrandMark from "../components/BrandMark";
import Footer from "../components/Footer";
import ResearchAccessForm from "../components/research/ResearchAccessForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Research() {
  const [published, setPublished] = useState(false);

  useEffect(() => {
    axios.get(`${API}/research/reports`)
      .then((r) => setPublished(Object.values(r.data).some((v) => v.available)))
      .catch(() => {});
    if (window.location.hash === "#lead-form") {
      setTimeout(() => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const scrollToForm = () => {
    document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header — same shape as Landing */}
      <header className="border-b border-black/10">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 md:px-8 py-4 md:py-5">
          <BrandMark />
          <div className="flex items-center gap-3 sm:gap-5 text-sm">
            <Link to="/" className="text-foreground/60 hover:text-foreground" data-testid="nav-diagnostic">
              Диагностика
            </Link>
            <span className="text-foreground" data-testid="nav-research-current">Исследование</span>
            <Link to="/admin/login" className="hop-link hidden md:inline" data-testid="admin-login-link">
              Вход для аналитиков
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-screen-xl mx-auto px-6 sm:px-8 py-16 sm:py-24 md:py-32 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-2">
            <div className="label-mono">RESEARCH / 2026</div>
          </div>
          <div className="md:col-span-9">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-black/20 mb-6 md:mb-8" data-testid="research-status-badge">
              <span className="w-1.5 h-1.5 bg-[hsl(var(--accent))] rounded-full" />
              <span className="label-mono">{published ? "Опубликовано" : "В стадии подготовки"}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-medium leading-[1.1] md:leading-[1.05] tracking-tight break-words">
              Рынок стартапов в России:<br className="hidden md:inline" />
              {" "}дифференцированный спрос
            </h1>
            <p className="mt-6 md:mt-8 text-base sm:text-lg md:text-xl text-foreground/70 max-w-3xl leading-relaxed">
              Первое исследование, которое рассматривает российскую стартап-индустрию
              не как единую воронку, а через призму трёх независимых типов спроса —
              инвесторского, корпоративного и институционального. Совместная работа
              Hop.Agency и StartupDrive.
            </p>
            <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <button className="btn-primary" onClick={scrollToForm} data-testid="research-cta-scroll">
                Получить исследование <ArrowRight size={16} />
              </button>
              <span className="label-mono">{published ? "Краткая · 25+ стр. / Полная · 150+ стр." : "Публикация · скоро"}</span>
            </div>
          </div>
        </section>

        {/* Three demand types */}
        <section className="border-t border-black/10">
          <div className="max-w-screen-xl mx-auto grid md:grid-cols-3">
            {[
              {
                num: "01",
                title: "Инвесторский спрос",
                icon: Users,
                desc: "Кого выбирают фонды: зрелость проекта, трекшн, юридическая упаковка, IP-барьеры, стратегия выхода.",
              },
              {
                num: "02",
                title: "Корпоративный спрос",
                icon: Building2,
                desc: "Что покупают корпорации: готовность к пилоту, интегрируемость, измеримый бизнес-эффект, соответствие ИБ-требованиям.",
              },
              {
                num: "03",
                title: "Институциональный спрос",
                icon: Landmark,
                desc: "На что смотрят институты развития: технологическая новизна, локализация, R&D-глубина, стратегический эффект.",
              },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.num}
                  className="grid-cell border-r-0 last:border-r border-l-0 first:border-l-0 md:border-l py-10 md:py-16 px-6 sm:px-8 md:px-10"
                >
                  <div className="flex items-center justify-between">
                    <div className="label-mono">DEMAND {c.num}</div>
                    <Icon size={20} className="text-foreground/40" />
                  </div>
                  <h3 className="text-2xl md:text-3xl mt-4 font-medium">{c.title}</h3>
                  <p className="mt-4 text-foreground/70 leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* What's inside */}
        <section className="max-w-screen-xl mx-auto px-6 sm:px-8 py-16 md:py-24 grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="label-mono">СОДЕРЖАНИЕ</div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl mt-3 font-medium">Что вошло в исследование</h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-4">
            {[
              "Модель трёх типов спроса и способ их независимого измерения",
              "Рейтинг отраслей по каждому типу спроса — 50+ сегментов",
              "Углублённый разбор доменов: Enterprise Solution, AI/ML, Cybersecurity, MedTech, RoboTech, EdTech",
              "Индикаторы зрелости, критические для перехода из MVP в масштабирование",
              "Практические рекомендации для основателей, инвесторов и корпораций",
            ].map((line, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check size={18} className="mt-1 text-[hsl(var(--accent))] shrink-0" />
                <span className="text-foreground/85 leading-relaxed">{line}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Methodology strip */}
        <section className="border-t border-b border-black/10 bg-[hsl(var(--secondary))]">
          <div className="max-w-screen-xl mx-auto px-6 sm:px-8 py-12 md:py-16 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { k: "50+", v: "Отраслей и сегментов" },
              { k: "50+", v: "Глубинных интервью" },
              { k: "3", v: "Независимых индекса спроса" },
              { k: "2026", v: "Год публикации" },
            ].map((stat) => (
              <div key={stat.v}>
                <div className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight tabular">{stat.k}</div>
                <div className="mt-2 text-sm text-foreground/60">{stat.v}</div>
              </div>
            ))}
          </div>
        </section>

        <ResearchAccessForm />
      </main>

      <Footer />
    </div>
  );
}

